import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useIsOperator } from '@/lib/useIsOperator';
import {
  LayoutDashboard, Briefcase, FileText, Send, Zap,
  User as UserIcon, LogOut, Menu, ChevronDown, HelpCircle,
  ChevronLeft, ChevronRight, Shield, Database, Building2, Inbox,
  Copy, Settings as SettingsIcon, Users as UsersIcon, Coins,
  Wallet, BarChart3, ShieldCheck, Archive, Sparkles,
  UserCheck, ClipboardList, BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import CreditsBalancePill from '@/components/CreditsBalancePill';
import EmployerPlanPill from '@/components/employer/EmployerPlanPill';
import ScoreVarianceHelpDrawer from '@/components/ScoreVarianceHelpDrawer';
import { CreditsProvider, useCredits } from '@/lib/creditsContext';
import BrandLogo from '@/components/BrandLogo';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import LegalFooter from '@/components/LegalFooter';
import SessionExpiredModal from '@/components/SessionExpiredModal';
import { useProfile } from '@/hooks/useProfile';
import { apiFetch } from '@/lib/api';

/**
 * RC 1.21 Information Architecture — now workspace-scoped (RC 1.30.B0).
 *
 * Each workspace has its own primary/secondary/account nav sections.
 * The sidebar filters by `currentWorkspace` so a user only ever sees
 * the nav items for the workspace they're currently in. Multi-
 * workspace users get a switcher at the top of the sidebar.
 */
const CANDIDATE_MAIN_NAV = [
  { label: 'Dashboard',    path: '/dashboard',    icon: LayoutDashboard },
  { label: 'Jobs',         path: '/jobs',         icon: Briefcase       },
  { label: 'Documents',    path: '/documents',    icon: FileText        },
  { label: 'Applications', path: '/applications', icon: Send            },
  { label: 'Signals',      path: '/candidate/signals', icon: Inbox      },
];

const CANDIDATE_TOOLS_NAV = [
  // RC 1.49.x — Workflow is plan-gated. Base users have no
  // background scoring, no review queue, and no Top Opportunities
  // surface. Showing Workflow on Base creates a dead-end. We hide
  // the nav entry entirely; the route still resolves so a direct
  // link surfaces a clean upgrade-locked card (handled by the page).
  { label: 'Workflow',     path: '/automation',    icon: Zap, requiresPlan: ['focus', 'flow'] },
  // RC 1.49.5 — promote in-app walkthrough to the main sidebar so
  // first-run users can find it without hunting in the avatar
  // dropdown. Dropdown link is retained as a redundant entry-point.
  { label: 'How it works', path: '/how-it-works',  icon: HelpCircle, testId: 'nav-how-it-works-link' },
];

const CANDIDATE_ACCOUNT_NAV = [
  // RC 1.35.F3 follow-up — expose /credits in the candidate nav.
  // The page was shipped in RC 1.35.F3 P1.17 but never wired into
  // the sidebar. Sits next to Account because both surfaces are
  // billing/settings adjacent.
  { label: 'Credits', path: '/credits', icon: Coins, testId: 'nav-credits-link' },
  { label: 'Account', path: '/account', icon: UserIcon },
  // RC 1.63 — public-facing candidate user guide (in-app + PDF).
  { label: 'Candidate User Guide', path: '/help/candidate', icon: BookOpen,
    testId: 'nav-help-candidate-link' },
];

// Employer workspace gets a deliberately narrow secondary section.
// Candidate Credits + /account are intentionally absent — employer
// billing is "Plan & Usage" surfaced inside the Account & usage
// page. The three items below match the command-centre brief's
// "Secondary" group: Signals · Account · Help.
//
// IA cleanup (2026-02-26): the second entry was previously labelled
// "Settings". The page it routes to (/employer/account) is titled
// "Account & usage" and contains zero settings controls, so the
// label was renamed to "Account" to match the page H1 and avoid
// hardening the misnomer once F.4.4 billing controls land. URL
// unchanged; testId preserved.
const EMPLOYER_ACCOUNT_NAV = [
  { label: 'Signals',  path: '/employer/signals', icon: Inbox,
    testId: 'sidebar-employer-signals-link' },
  { label: 'Account',  path: '/employer/account', icon: SettingsIcon,
    testId: 'sidebar-employer-settings-link' },
  // PHASE-A (2026-02-26) — In-app "How it works" workflow primer.
  // Lives under the Account/help section alongside the permissions
  // matrix help page so the IA doctrine (operational primaries on
  // top; help/reference under Account) is preserved.
  { label: 'How it works', path: '/employer/help/workflow', icon: BookOpen,
    testId: 'sidebar-employer-workflow-link' },
  { label: 'Help',     path: '/employer/help/permissions', icon: HelpCircle,
    testId: 'sidebar-employer-permissions-link' },
  // RC 1.63.1 — Doctrine-aligned guide separation. Two distinct
  // labels (no generic "User guide" duplicates) with strict role
  // gates so the help surface mirrors the governance model:
  //   • Employer User Guide — workspace/lifecycle/entitlements
  //     governance. Owners + admins only.
  //   • Reviewer User Guide — reviewer doctrine + process. Visible
  //     to every employer-workspace member so each role sees the
  //     reviewer protocol they collaborate with.
  { label: 'Employer User Guide', path: '/help/employer', icon: BookOpen,
    testId: 'sidebar-employer-user-guide-link',
    requiresMemberRole: ['owner', 'admin'] },
  { label: 'Reviewer User Guide', path: '/help/reviewer', icon: BookOpen,
    testId: 'sidebar-reviewer-user-guide-link',
    requiresMemberRole: ['owner', 'admin', 'recruiter', 'reviewer'] },
];

// Admin workspace has no candidate-billing carryover. Admins reach
// commercial surfaces through /admin/billing (already in
// ADMIN_MAIN_NAV) and personal settings through /admin/settings.
const ADMIN_ACCOUNT_NAV = [];

const EMPLOYER_MAIN_NAV = [
  // 2026-02-26 — Command Centre IA refactor. /employer is now the
  // operational dashboard; the organisation profile + governance
  // hub moved to /employer/organisation. Sidebar order matches the
  // command-centre brief: Dashboard · Roles · Reviews · Candidates
  // · Members · Organisation. Members was previously a sub-surface
  // of the Organisation hub; the 2026-02-26 IA cleanup promotes it
  // to a primary nav entry (owner/admin/recruiter gate) so the
  // higher-frequency members surface stops requiring two clicks
  // via Organisation. Reviewers admin remains a sub-page deep-
  // linked from Members + Command Centre.
  { label: 'Dashboard',    path: '/employer',            icon: LayoutDashboard,
    testId: 'sidebar-employer-dashboard-link' },
  { label: 'Roles',        path: '/employer/roles',      icon: Briefcase,
    requiresMemberRole: ['owner', 'admin', 'recruiter'] },
  { label: 'Reviews',      path: '/employer/reviews',    icon: ClipboardList,
    testId: 'sidebar-reviews-link',
    requiresMemberRole: ['reviewer'] },
  // RC EMPLOYER.IA.SLICE-1 (2026-02-27) — /employer/candidates entry
  // demoted from primary nav. The page is a placeholder (cross-role
  // candidate view is intentionally deferred; candidates live
  // strictly inside each role's Uploaded Dossiers tab today). The
  // route remains mounted in App.js so existing bookmarks redirect
  // gracefully to /employer/roles via the page's CTA — IA prominence
  // is dropped to align with the role-scoped doctrine.
  { label: 'Members',      path: '/employer/members',    icon: UsersIcon,
    testId: 'sidebar-employer-members-link',
    requiresMemberRole: ['owner', 'admin', 'recruiter'] },
  { label: 'Organisation', path: '/employer/organisation', icon: Building2,
    testId: 'sidebar-employer-organisation-link',
    requiresMemberRole: ['owner', 'admin'] },
];

const ADMIN_MAIN_NAV = [
  { label: 'Analytics',      path: '/admin/analytics',  icon: BarChart3 },
  { label: 'Scoring',        path: '/admin/scoring',    icon: ShieldCheck },
  { label: 'Jobs Inventory', path: '/admin/jobs',       icon: Shield   },
  { label: 'Duplicates',     path: '/admin/duplicates', icon: Copy     },
  { label: 'Sources',        path: '/admin/sources',    icon: Database },
  { label: 'Catalogue',      path: '/admin/catalogue',  icon: Building2 },
  { label: 'Users',          path: '/admin/users',      icon: UsersIcon },
  { label: 'Employers',      path: '/admin/employers',  icon: Building2,
    testId: 'sidebar-employers-link' },
  { label: 'Billing',        path: '/admin/billing',    icon: Wallet   },
  { label: 'Retention',      path: '/admin/retention',  icon: Archive  },
  { label: 'Settings',       path: '/admin/settings',   icon: SettingsIcon },
];

const pageTitles = {
  '/dashboard':       'Dashboard',
  '/jobs':            'Jobs',
  '/documents':       'Documents',
  '/applications':    'Applications',
  '/account':         'Account',
  '/credits':         'Credits',
  '/automation':      'Workflow',
  '/how-it-works':    'How it works',
  '/candidate/signals': 'Signals',
  '/employer':              'Command Centre',
  '/employer/organisation': 'Organisation',
  '/employer/candidates':   'Candidates',
  '/employer/signals':      'Signals',
  '/employer/account':      'Account',
  '/employer/roles':        'Roles',
  '/employer/members':      'Members',
  '/employer/reviewers':    'Reviewers',
  '/employer/reviews':      'Reviews',
  '/employer/help/workflow':    'How it works',
  '/employer/help/permissions': 'Permissions',
  '/admin/analytics': 'Operator Analytics',
  '/admin/scoring':   'Scoring Governance',
  '/admin/jobs':      'Jobs Inventory',
  '/admin/duplicates': 'Duplicate clusters',
  '/admin/sources':   'Source Freshness',
  '/admin/catalogue': 'Employer Catalogue',
  '/admin/users':     'User management',
  '/admin/employers': 'Employers',
  '/admin/billing':   'Billing & Pricing',
  '/admin/retention': 'Artefact Retention',
  '/admin/settings':  'Runtime Settings',
  '/choose-workspace': 'Choose workspace',
  '/no-access':        'No access',
};

function NavItem({ item, collapsed, onClick, badge }) {
  const content = ({ isActive }) => (
    <span
      className={`group relative flex items-center gap-x-3 rounded-md px-3 py-2.5 text-sm transition-colors
        ${isActive
          ? 'bg-ndor-surface-alt text-ndor-ink font-semibold'
          : 'text-ndor-ink-secondary hover:bg-ndor-surface-alt/60 hover:text-ndor-ink'}
        ${collapsed ? 'justify-center px-2' : ''}`}
    >
      {isActive && (
        <span
          aria-hidden
          className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-ndor-gold"
        />
      )}
      <item.icon
        className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'text-ndor-gold' : 'text-ndor-ink-muted group-hover:text-ndor-ink-secondary'}`}
        strokeWidth={1.6}
      />
      {!collapsed && <span className="tracking-tight">{item.label}</span>}
      {/* P1.1 — pending-count badge. 1–9 shows integer; >9 shows 9+.
          No animation. No pulse. Sits at the end of the row or top-
          right when collapsed. */}
      {badge > 0 && (
        <span
          className={
            collapsed
              ? 'absolute top-1 right-1 state-pill state-pill--pending !px-1 !py-0 !text-[9px] !font-semibold'
              : 'ml-auto state-pill state-pill--pending !py-0 !text-[10px] !font-semibold'
          }
          data-testid={`sidebar-${item.label.toLowerCase()}-badge`}
        >
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </span>
  );

  const link = (
    <NavLink
      to={item.path}
      onClick={onClick}
      data-testid={item.testId || `sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
    >
      {content}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="bg-ndor-surface-alt text-ndor-ink border-ndor-edge">
        {item.label}
        {badge > 0 ? ` — ${badge > 9 ? '9+' : badge} pending` : ''}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * P1.1 — thin hook. Polls the already-existing candidate signals
 * endpoint for `counts.pending` whenever we're in the candidate
 * workspace. No new backend. Refreshes on mount, on workspace
 * change, and on location change so responding to a signal reflects
 * in the sidebar without a reload.
 */
function useCandidatePendingCount(currentWorkspace, locationKey, enabled = true) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    // RC 1.49.7 (2026-05-09) — operator-only Signals visibility.
    // The Signals surface is hidden from non-operators while the
    // employer-side outbound workflow is still pre-launch. Skipping
    // the count fetch when the nav entry is hidden also avoids
    // wasting a request per location change.
    if (!enabled) { setCount(0); return; }
    if (currentWorkspace !== 'candidate') { setCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch('/api/candidate/signals');
        if (!cancelled) {
          setCount((data?.counts?.pending | 0) || 0);
        }
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [currentWorkspace, locationKey, enabled]);
  return count;
}

/**
 * Pass-1 Beta Activation — Slice 1 sidebar gating.
 *
 * Reads the caller's member role on the current employer account
 * from the existing `/api/employer/v1/me` shape. One read per
 * workspace mount; failures degrade silently (sidebar shows no
 * role-gated entries). This hook is intentionally tiny and
 * read-only — no writes, no caching beyond component lifetime.
 */
function useEmployerMemberRole(currentWorkspace) {
  const [role, setRole] = useState(null);
  useEffect(() => {
    if (currentWorkspace !== 'employer') { setRole(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const me = await apiFetch('/api/employer/v1/me');
        if (!cancelled) setRole(me?.member?.role || null);
      } catch {
        if (!cancelled) setRole(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentWorkspace]);
  return role;
}

/**
 * RC 1.62 Reviewer Dashboard CTA — sidebar pending-review counter.
 *
 * Polls `/api/employer/v1/reviewer/queue` (RLS self-scoped) for the
 * reviewer's own pending count whenever the user is in the employer
 * workspace AND holds the reviewer role. Refreshes on mount and on
 * route change so a decision taken inside the Reviews surface
 * reflects immediately in the sidebar badge. Failure degrades to
 * zero — never blocks the sidebar render.
 */
function useReviewerPendingCount(currentWorkspace, locationKey, memberRole) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (currentWorkspace !== 'employer') { setCount(0); return; }
    if (memberRole !== 'reviewer')      { setCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch('/api/employer/v1/reviewer/queue?limit=200');
        if (cancelled) return;
        const rows = Array.isArray(data?.reviews) ? data.reviews : [];
        setCount(rows.filter((r) => r?.status === 'pending').length);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [currentWorkspace, locationKey, memberRole]);
  return count;
}

function SidebarContent({ collapsed, onNavClick, currentWorkspace, signalsPending, reviewerPending, isOperator, employerMemberRole }) {
  // RC 1.49.x — pull the cached plan code so we can hide the
  // Workflow nav for base users (no background scoring → no use
  // for the Workflow surface). One read; no extra API call.
  const { planCode } = useCredits();
  // Pick the nav sets based on which workspace the user is currently
  // looking at. Each workspace owns its own IA; we never render
  // cross-workspace links in the sidebar.
  let mainItems = [];
  let toolsItems = [];
  let accountItems = [];
  let sectionLabel = 'Workspace';
  if (currentWorkspace === 'candidate') {
    // RC 1.49.7 (2026-05-09) — operator-only Signals nav. The
    // candidate Signals surface is hidden from non-operators while
    // the Employers-side outbound interest workflow is still pre-
    // launch. The route itself ALSO redirects non-operators to
    // /dashboard (see App.js), so a direct visit can't bypass this.
    mainItems    = CANDIDATE_MAIN_NAV.filter(
      (it) => it.path !== '/candidate/signals' || isOperator,
    );
    // RC 1.49.x — operator-locked plan gate on Tools nav. Items
    // declaring `requiresPlan` only render when the user's
    // `planCode` is in that list. Unknown / null plan → treat as
    // base (no Workflow), so first paint never flashes a Workflow
    // entry the user can't actually use.
    toolsItems   = CANDIDATE_TOOLS_NAV.filter((it) => {
      if (!it.requiresPlan) return true;
      return it.requiresPlan.includes(planCode || 'base');
    });
    accountItems = CANDIDATE_ACCOUNT_NAV;
    sectionLabel = 'Workspace';
  } else if (currentWorkspace === 'employer') {
    // Slice 1 — filter reviewer-side nav entries by member role.
    mainItems    = EMPLOYER_MAIN_NAV.filter((it) => {
      if (!it.requiresMemberRole) return true;
      return employerMemberRole && it.requiresMemberRole.includes(employerMemberRole);
    });
    // 2026-02-26 — workspace navigation leak fix (Patch A). The
    // employer workspace previously reused CANDIDATE_ACCOUNT_NAV,
    // which exposed /credits and /account links — both surfaces are
    // candidate-only. Employer-only account section now carries the
    // Permissions help link.
    //
    // RC 1.63.1 (2026-05-30) — the account/help section also carries
    // the two doctrine-aligned user-guide entries (Employer User
    // Guide owner/admin only; Reviewer User Guide all roles). The
    // member-role gate has to be applied here too, not only on the
    // main nav, or the gates declared in EMPLOYER_ACCOUNT_NAV are
    // silently ignored and recruiters/reviewers see the Employer
    // User Guide entry they shouldn't.
    accountItems = EMPLOYER_ACCOUNT_NAV.filter((it) => {
      if (!it.requiresMemberRole) return true;
      return employerMemberRole && it.requiresMemberRole.includes(employerMemberRole);
    });
    sectionLabel = 'Employer';
  } else if (currentWorkspace === 'admin') {
    mainItems    = ADMIN_MAIN_NAV;
    // 2026-02-26 — same leak fix for admin workspace (Patch A).
    accountItems = ADMIN_ACCOUNT_NAV;
    sectionLabel = 'Admin';
  }

  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-5'} pt-2 pb-8`}>
        <BrandLogo variant="symbol" size={collapsed ? 40 : 44} />
        {!collapsed && (
          <div className="flex flex-col leading-none">
            <span className="text-[22px] font-serif-display font-semibold tracking-tight text-ndor-ink">
              NDOR<span className="text-gold-accent">Flow</span>
            </span>
            <span className="text-[9px] uppercase tracking-[0.22em] text-ndor-ink-secondary font-mono-ui mt-1.5">
              Decision Intelligence
            </span>
          </div>
        )}
      </div>

      {/* Workspace switcher — rendered only when the user has >=2
          allowed workspaces. */}
      <WorkspaceSwitcher collapsed={collapsed} />

      <nav
        className={`flex-1 ${collapsed ? 'px-2' : 'px-3'} space-y-1`}
        data-testid="primary-nav"
        data-workspace={currentWorkspace || 'unknown'}
      >
        {!collapsed && (
          <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.22em] uppercase text-ndor-ink-muted font-mono-ui">
            {sectionLabel}
          </p>
        )}
        {mainItems.map(item => (
          <NavItem
            key={item.path}
            item={item}
            collapsed={collapsed}
            onClick={onNavClick}
            badge={
              item.path === '/candidate/signals' ? signalsPending :
              item.path === '/employer/reviews'  ? reviewerPending :
              0
            }
          />
        ))}
        {toolsItems.length > 0 && (
          <>
            <Separator className="my-4 bg-ndor-edge" />
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.22em] uppercase text-ndor-ink-muted font-mono-ui">
                Tools
              </p>
            )}
            {toolsItems.map(item => (
              <NavItem key={item.path} item={item} collapsed={collapsed} onClick={onNavClick} />
            ))}
          </>
        )}
        {accountItems.length > 0 && (
          <>
            <Separator className="my-4 bg-ndor-edge" />
            {accountItems.map(item => (
              <NavItem key={item.path} item={item} collapsed={collapsed} onClick={onNavClick} />
            ))}
          </>
        )}
      </nav>

      <div className={`${collapsed ? 'px-2' : 'px-3'} pb-4 pt-2`}>
        {!collapsed && (
          <div className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/60 p-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-secondary font-mono-ui">
              Decision Intelligence
            </p>
            <p className="text-xs text-ndor-ink-secondary mt-1 leading-relaxed">
              Every action is logged to your ledger.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, signOut } = useAuth();
  const { profile, greetingName } = useProfile();
  const { currentWorkspace } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('ndor.sidebar.collapsed') === '1'; }
    catch (_) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('ndor.sidebar.collapsed', collapsed ? '1' : '0'); }
    catch (_) { /* ignore */ }
  }, [collapsed]);

  const pageTitle = pageTitles[location.pathname] || 'NDORFlow';
  const userInitial = (greetingName?.[0] || profile?.first_name?.[0] || user?.email?.[0] || 'U').toUpperCase();
  const userMenuLabel = greetingName || profile?.first_name || 'Account';

  // RC 1.62 Phase D completion (D-3, 2026-02-28) — operator-only
  // Signals visibility. The Signals page is hidden from
  // non-operators while the Employers-side outbound interest
  // workflow is pre-launch. The operator flag is now resolved by
  // the backend via `/api/auth/me/access::is_operator`; the
  // frontend no longer carries a hardcoded UUID/email.
  const { isOperator } = useIsOperator();

  // P1.1 — sidebar pending-signals badge.
  const signalsPending = useCandidatePendingCount(currentWorkspace, location.pathname, isOperator);
  const employerMemberRole = useEmployerMemberRole(currentWorkspace);
  // RC 1.62 — sidebar pending-review badge for reviewer members.
  const reviewerPending = useReviewerPendingCount(currentWorkspace, location.pathname, employerMemberRole);

  const handleLogout = async () => {
    await signOut();
  };

  const sidebarWidth = collapsed ? 'lg:w-[76px]' : 'lg:w-[260px]';
  const mainPadLeft  = collapsed ? 'lg:pl-[76px]' : 'lg:pl-[260px]';

  return (
    <CreditsProvider>
      <TooltipProvider delayDuration={300}>
        <div className="min-h-screen bg-ndor-canvas">
          {/* Desktop Sidebar */}
          <aside
            className={`fixed inset-y-0 left-0 z-50 hidden lg:flex flex-col ${sidebarWidth} border-r border-ndor-edge bg-ndor-surface pt-6 pb-4 transition-[width] duration-200 ease-out`}
            data-testid="desktop-sidebar"
            data-collapsed={collapsed ? 'true' : 'false'}
          >
            <SidebarContent collapsed={collapsed} currentWorkspace={currentWorkspace} signalsPending={signalsPending} reviewerPending={reviewerPending} isOperator={isOperator} employerMemberRole={employerMemberRole} />
            <button
              type="button"
              onClick={() => setCollapsed(v => !v)}
              className="absolute -right-3 top-20 h-6 w-6 rounded-full bg-ndor-surface-alt border border-ndor-edge text-ndor-ink-muted hover:text-ndor-ink flex items-center justify-center shadow-md"
              data-testid="sidebar-collapse-toggle"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
            </button>
          </aside>

          <div className={`${mainPadLeft} flex flex-col min-h-screen`}>
            {/* Top Bar */}
            <header
              className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-2 sm:gap-x-3 lg:gap-x-4 border-b border-ndor-edge bg-ndor-surface/80 backdrop-blur-xl px-3 sm:px-6 lg:px-8 min-w-0"
              data-testid="topbar"
            >
              {/* Mobile brand — symbol-only per RC 1.21 */}
              <div className="lg:hidden flex items-center gap-2 shrink-0" data-testid="mobile-brand">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-ndor-ink-secondary" data-testid="mobile-menu-button">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-64 p-0 pt-6 bg-ndor-surface border-ndor-edge">
                    <SidebarContent collapsed={false} currentWorkspace={currentWorkspace} signalsPending={signalsPending} reviewerPending={reviewerPending} isOperator={isOperator} employerMemberRole={employerMemberRole} onNavClick={() => setMobileOpen(false)} />
                  </SheetContent>
                </Sheet>
                <BrandLogo variant="symbol" size={28} />
              </div>

              {/* RC 1.53 (2026-05-12 PM, operator-locked) — the topbar
                  page-title was a small duplicate of each page's own
                  H1 (Jobs / Documents / Applications all render their
                  own page heading). The flex spacer is preserved so
                  the right-aligned credits / user menu still anchor
                  to the right of the topbar. */}
              <div className="flex-1 min-w-0" data-testid="page-title-spacer" />

              <div className="ml-auto flex shrink-0 items-center gap-x-2 sm:gap-x-3">
                <ScoreVarianceHelpDrawer />
                {/* 2026-02-26 — workspace navigation leak fix
                    (Patch B). CreditsBalancePill targets the
                    candidate billing dashboard (/account?tab=plan)
                    and renders candidate-only copy ("Plan/Credits/
                    Free fits today"). Render only inside the
                    candidate workspace so employer/admin surfaces
                    never expose candidate billing chrome.
                    2026-02-26 — Prompt 3 Commercial Surface Pass 1:
                    employer workspace gets its own operationally-
                    honest pill (Base/Focus/Flow/Enterprise + roles
                    usage; NEVER "Credits"). Admin workspace stays
                    pill-less. */}
                {currentWorkspace === 'candidate' && <CreditsBalancePill />}
                {currentWorkspace === 'employer'  && <EmployerPlanPill />}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-2 hover:bg-ndor-surface-alt" data-testid="user-menu-trigger">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-ndor-surface-alt text-ndor-ink-secondary text-sm font-medium border border-ndor-edge">
                          {userInitial}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden md:block text-sm text-ndor-ink-secondary max-w-[140px] truncate" data-testid="topbar-user-label">
                        {userMenuLabel}
                      </span>
                      <ChevronDown className="h-4 w-4 text-ndor-ink-muted" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-ndor-surface border-ndor-edge">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-sm font-medium text-ndor-ink" data-testid="usermenu-name">
                        {greetingName || profile?.first_name || 'Account'}
                      </p>
                      <p className="text-xs text-ndor-ink-secondary truncate">{user?.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-ndor-edge" />
                    {/* 2026-02-26 — profile dropdown workspace-aware
                        gating. Candidate items ("How it works",
                        "Show first-run guide") are candidate-flow
                        prompts; employer/admin must not see them.
                        Employer gets links to two existing, safe,
                        static surfaces — Roles & permissions and
                        Account & Usage. Admin gets neither (admin
                        users navigate via the admin sidebar). */}
                    {currentWorkspace === 'candidate' && (
                      <DropdownMenuItem
                        onClick={() => navigate('/how-it-works')}
                        data-testid="usermenu-how-it-works"
                        className="cursor-pointer"
                      >
                        <HelpCircle className="mr-2 h-4 w-4" />
                        How it works
                      </DropdownMenuItem>
                    )}
                    {/* RC 1.49.x reviewer-trust polish — re-open the
                        first-run mental-model splash on demand. The
                        Dashboard listens for `ndor:first-run-open`
                        and shows the dialog next time it mounts.
                        We navigate to /dashboard first so the dialog
                        is guaranteed to be in the tree. */}
                    {currentWorkspace === 'candidate' && (
                      <DropdownMenuItem
                        onClick={() => {
                          navigate('/dashboard');
                          // Defer so the dashboard's effect listener
                          // is mounted before we fire the event.
                          setTimeout(() => {
                            window.dispatchEvent(new Event('ndor:first-run-open'));
                          }, 0);
                        }}
                        data-testid="usermenu-show-first-run"
                        className="cursor-pointer"
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Show first-run guide
                      </DropdownMenuItem>
                    )}
                    {currentWorkspace === 'employer' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate('/employer/help/permissions')}
                          data-testid="usermenu-employer-permissions"
                          className="cursor-pointer"
                        >
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          Roles &amp; permissions
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/employer/account')}
                          data-testid="usermenu-employer-account"
                          className="cursor-pointer"
                        >
                          <Building2 className="mr-2 h-4 w-4" />
                          Account &amp; Usage
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={handleLogout}
                      data-testid="logout-button"
                      className="text-ndor-error focus:text-ndor-error cursor-pointer"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </header>

            <main className="flex-1 px-3 py-4 sm:p-6 lg:p-8 pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="main-content">
              {/* RC 1.55 P0 Mobile pass (2026-05-12 PM, operator-locked):
                  – RC1475OperatorBanner removed from the render tree.
                    The Pack Canonical Fit rollout announcement is
                    obsolete.
                  – RC 1.56 P5 (operator-locked, 2026-05-13 PM):
                    component file + dead import also deleted —
                    six months on, the banner has no realistic
                    revival path. The `isCanonicalFitUiEnabled` flag
                    in `lib/rc_1_47_5_flag.js` REMAINS in use by
                    `ScoreVarianceHelpDrawer` and
                    `wizard/Step3ScoreLandscape` (legitimate feature
                    gates) so the flag file is preserved.
                  – Outer padding reduced on narrow viewports
                    (`px-3 py-4`) so phone gutters don't eat the
                    content width; `sm:p-6 lg:p-8` restores the
                    breathing room on tablet + desktop.
                  – `pb-[calc(env(safe-area-inset-bottom)+1rem)]`
                    reserves space for browser chrome / home-indicator
                    so sticky footers never sit under the iOS Safari
                    URL bar / Android nav bar. */}
              <div
                className="mx-auto w-full max-w-5xl"
                data-testid="main-content-container"
              >
                <Outlet />
              </div>
            </main>
            <LegalFooter />
            <SessionExpiredModal />
          </div>
        </div>
      </TooltipProvider>
    </CreditsProvider>
  );
}
